<template>
  <div class="w-full flex justify-center">
    <div class="main-container">
      <zt-button
        color="dark-400"
        icon="angle-left"
        text
        @click="activeDialog(true)"
        class="go-back"
      >
        {{ $t('message.selfService.linkBack') }}
      </zt-button>
      <div class="heading pb-6 flex items-center">
        <h2 class="font-normal text-zt-primary-200">
          {{ $t('message.selfService.title') }}
        </h2>
      </div>
      <div class="main rounded">
        <div class="form-container relative w-full h-full">
          <div v-if="!isLoading">
            <div class="px-32 py-16">
              <h2 class="pb-12 font-bold">
                {{ $t('message.selfService.subTitle') }}
              </h2>
              <enable-contact-update />
              <enable-bank-data-update />
              <enable-identifies-update />
              <enable-address-update />
            </div>
            <div class="footer flex justify-end items-center px-10 h-24">
              <zt-button
                text
                class="button-cancel mr-4"
                @click="activeDialog(true)"
              >
                {{ $t('message.selfService.linkCancel') }}
              </zt-button>
              <zt-button
                :disabled="!changed"
                :loader="loadingButton"
                :class="{
                  'text-zt-dark-b6': !changed,
                  'text-zt-dark-200': !changed
                }"
                @click="save"
                >{{ $t('message.selfService.linkSave') }}</zt-button
              >
            </div>
          </div>
          <div class="w-full h-full" v-else>
            <vue-element-loading
              :active="isLoading"
              spinner="spinner"
              duration="1.5"
              color="#4f4f4f"
              size="50"
            />
          </div>
        </div>
      </div>
    </div>
    <cancel-configuration-dialog
      ref="modal"
      :is-leave="isLeave"
      :active="cancel"
    />
    <error-dialog :message="messageError" :active="isActiveErrorDialog" />
    <success-dialog :active="isActiveSuccessDialog" />
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading'
import EnableContactUpdate from '@/modules/SelfService/components/configuration/EnableContactUpdate'
import EnableBankDataUpdate from '@/modules/SelfService/components/configuration/EnableBankDataUpdate'
import EnableIdentifiesUpdate from '@/modules/SelfService/components/configuration/EnableIdentifiesUpdate'
import EnableAddressUpdate from '@/modules/SelfService/components/configuration/EnableAddressUpdate'
import CancelConfigurationDialog from '@/modules/SelfService/components/modals/CancelConfigurationDialog'
import ErrorDialog from '@/modules/SelfService/components/modals/ErrorDialog'
import SuccessDialog from '@/modules/SelfService/components/modals/SuccessDialog'
import useConfigurationState from '@/modules/SelfService/hooks/useConfigurationState'
import Actions from '@/apiClient/Actions'
import { isEqual, clone, findIndex } from 'lodash'

const {
  state: configurationState,
  resetParams,
  setParams,
  updateParams
} = useConfigurationState()
export default {
  name: 'SelfServiceConfiguration',
  components: {
    VueElementLoading,
    EnableContactUpdate,
    EnableBankDataUpdate,
    EnableIdentifiesUpdate,
    EnableAddressUpdate,
    CancelConfigurationDialog,
    ErrorDialog,
    SuccessDialog
  },
  provide() {
    return {
      activeDialog: this.activeDialog,
      activeErrorDialog: this.activeErrorDialog,
      activeSuccessDialog: this.activeSuccessDialog,
      configurationState,
      hasHistory: this.hasHistory,
      resetParams,
      setParams
    }
  },
  data() {
    return {
      cache: {},
      cancel: false,
      configurationState,
      changed: false,
      isActiveErrorDialog: false,
      isLoading: true,
      isLeave: false,
      isActiveSuccessDialog: false,
      loadingButton: false,
      messageError: '',
      isCompanyFromArgentina: false
    }
  },
  watch: {
    configurationState: {
      handler() {
        const isAddressEnabled = this.configurationState.params.address.enable
        const isDocumentTypeEmpty =
          this.configurationState.idtypedocument === ''
        const isCompanyFromArgentina = this.isCompanyFromArgentina
        const isFolderEmpty = this.configurationState.idfolder === ''

        const isFromArgentinaAndEnableAdress =
          isAddressEnabled && isCompanyFromArgentina
        const isFolderOrTypeNotSelected = isDocumentTypeEmpty || isFolderEmpty

        this.changed = !isEqual(this.cache, this.configurationState.params)

        if (isFromArgentinaAndEnableAdress && isFolderOrTypeNotSelected) {
          this.changed = false
        }
      },
      deep: true
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.changed) {
      this.isLeave = true
      this.activeDialog(true)
    }
    next()
  },
  async created() {
    this.actionsApi = new Actions()
    this.isCompanyFromArgentina =
      this.$store.state.country === 'ar' ? true : false
    await this.getSelfServiceActions()
  },
  methods: {
    async activeDialog(active) {
      if (!this.changed) {
        await resetParams()
        this.hasHistory() ? this.$router.go(-1) : this.$router.push('/')
        return
      }
      if (!active) this.isLeave = false
      this.cancel = active
    },
    activeErrorDialog(active) {
      this.isActiveErrorDialog = active
    },
    async activeSuccessDialog(active) {
      this.isActiveSuccessDialog = active
      if (!active) await this.getSelfServiceActions()
    },
    async getSelfServiceActions() {
      this.isLoading = true
      try {
        const {
          data: { data: actions }
        } = await this.actionsApi.getActionsSelfService()
        if (actions) {
          for (const [name, properties] of Object.entries(
            this.configurationState.params
          )) {
            const index = findIndex(
              actions,
              (action) => parseInt(action.idaccion) === properties.id
            )
            await updateParams(name, { enable: index > -1 })
          }
          const foundAction = actions.find(
            (item) => item.idaccion === '204' && item.idfolder !== undefined
          )
          if (foundAction) {
            const { idfolder } = foundAction
            await setParams('idfolder', { idfolder })
            if (foundAction.idtypedocument !== undefined) {
              const { idtypedocument } = foundAction
              await setParams('idtypedocument', { idtypedocument })
            }
          }
        }
      } catch (e) {}
      this.cache = clone(this.configurationState.params)
      this.changed = false
      this.isLoading = false
    },
    hasHistory() {
      return window.history.length > 2
    },
    async save() {
      try {
        this.loadingButton = true
        const {
          post: postIds,
          delete: deleteIds,
          idtypedocument
        } = this.configurationState
        if (deleteIds.length)
          await this.actionsApi.removeActionSelfService({
            idaction: JSON.stringify(deleteIds)
          })
        if (postIds.length) {
          const params = { idaction: postIds }
          if (this.configurationState.idtypedocument !== '') {
            params.idtypedocument = this.configurationState.idtypedocument
          }
          await this.actionsApi.setActionSelfService(params)
        }
        if (
          idtypedocument !== '' &&
          (!this.cache.idtypedocument ||
            idtypedocument !== this.cache.idtypedocument.idtypedocument)
        ) {
          const params = {
            idaction: [this.cache.address.id],
            idtypedocument: idtypedocument
          }
          await this.actionsApi.setActionSelfService(params)
        }

        this.loadingButton = false
        await resetParams()
        this.activeSuccessDialog(true)
      } catch (e) {
        if (e.response && e.response.status === 400) {
          const { code } = e.response.data.codes[0]
          
          this.messageError = this.$t(`message.selfService.errors.${code}`)
          this.activeErrorDialog(true)
        }
        this.loadingButton = false
      }
    }
  }
}
</script>
<style scoped lang="scss">
.main-container {
  width: 844px;
  height: calc(100vh - 155px);
  h2 {
    font-size: 24px;
  }
  .heading {
    height: 50px;
    h2 {
      color: #707070;
    }
  }
  .main {
    height: calc(100% - 50px);
    background: white;
    box-shadow: 0 3px 4px rgba(0, 0, 0, 0.14), 0 3px 3px rgba(0, 0, 0, 0.12),
      0 1px 8px rgba(0, 0, 0, 0.2);
    overflow: hidden;
  }
}
.form-container {
  overflow-y: auto;
}
.form-container::-webkit-scrollbar-track {
  background-color: #c4c4c4;
}
.form-container::-webkit-scrollbar {
  width: 0.625rem;
}
.form-container::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background-color: #888888;
}
.footer {
  background: #e5e5e5;
  border-top: 1px solid #c4c4c4;
  font-size: 1.125rem;
  .text-zt-dark-b6 {
    color: #b6b6b6 !important;
  }
  ::v-deep {
    .button-cancel {
      color: #707070 !important;
      background: transparent !important;
    }
  }
}
::v-deep {
  .zt-button.go-back {
    background: none !important;
    color: #707070 !important;
    padding: 4px !important;
    margin: 0;
    font-size: 1.2rem !important;
    span:first-child {
      width: 10px;
    }
    span.zt-button-text {
      margin-top: 2px;
    }
  }
}
</style>
